import { useState } from 'react';
import { twMerge } from 'tailwind-merge';

import DownloadIcon from '../../../assets/svg/DownloadIcon';
import RefreshIcon from '../../../assets/svg/RefreshIcon';
import { TABS } from '../../../constants';
import { Button, Loader } from '../../Shared';
import { FiltersPanel } from '../FiltersPanel';
import { NumberRecords } from '../NumberRecords';

export const STATE = {
  HAS_TABLE_DATA: 'hasTableData',
  IS_TABLE_DATA_LOADING: 'isTableDataLoading',
  IS_TABLE_DATA_ERROR: 'isTableDataError',
};

export const getState = (isTableDataLoading, isTableDataError) => {
  if (isTableDataLoading) return STATE.IS_TABLE_DATA_LOADING;
  if (isTableDataError) return STATE.IS_TABLE_DATA_ERROR;
  return STATE.HAS_TABLE_DATA;
};

/*
  INFO: 
    tableData.metrics.length - 1 because we need to exclude TOTAL.
    TOTAL row contains aggregated values across all data. 
*/
export const TabContent = ({
  activeTab,
  onChangeActiveTab,
  filters,
  isFiltersChanged,
  onChangeFilters,
  resetFilters,
  clients,
  isClientsLoading,
  isClientsError,
  tableData,
  isTableDataLoading,
  isTableDataError,
  refetchTableData,
  renderTable,
  onExportPDF,
}) => {
  const [isExportPDFSubmitting, setIsExportPDFSubmitting] = useState(false);

  const exportPDF = async () => {
    setIsExportPDFSubmitting(true);
    await onExportPDF(tableData);
    setIsExportPDFSubmitting(false);
  };

  const hasTableData = !!tableData.metrics && tableData.metrics.length - 1 > 0;
  const isButtonExportPDFDisabled =
    isExportPDFSubmitting ||
    isTableDataLoading ||
    isTableDataError ||
    !hasTableData;
  const state = getState(isTableDataLoading, isTableDataError);

  return (
    <div className="flex flex-col gap-4 h-full overflow-hidden">
      <div className="flex justify-between gap-3 overflow-auto shrink-0">
        <div className="flex gap-3 shrink-0">
          <div className="flex p-1 bg-brand-100/80 rounded-full">
            {TABS.map(({ id, title }) => {
              const isActive = activeTab === id;

              return (
                <div
                  key={id}
                  className={twMerge(
                    'py-2 px-5 rounded-full cursor-pointer',
                    isActive ? 'bg-brand-900' : 'bg-inherit',
                  )}
                  onClick={() => {
                    onChangeActiveTab(id);
                  }}
                >
                  <span
                    className={twMerge(
                      'font-medium text-[16px]/[24px]',
                      isActive ? 'text-white' : 'text-base-1000',
                    )}
                  >
                    {title}
                  </span>
                </div>
              );
            })}
          </div>
          <FiltersPanel
            filters={filters}
            onChangeFilters={onChangeFilters}
            clients={clients}
            isClientsLoading={isClientsLoading}
            isClientsError={isClientsError}
          />
          {isFiltersChanged && (
            <Button
              title="Clear All"
              variant="text"
              onClick={resetFilters}
              className="!bg-transparent hover:!bg-transparent focus:!bg-transparent text-brand-900 p-2"
            />
          )}
        </div>
        <div className="flex gap-3 shrink-0">
          {hasTableData && (
            <NumberRecords value={tableData.metrics.length - 1} />
          )}
          <Button
            title="Export PDF"
            variant="blue"
            iconStart={
              <DownloadIcon
                className={twMerge(
                  'w-5 h-5 text-brand-900',
                  isButtonExportPDFDisabled && 'text-base-400',
                )}
              />
            }
            disabled={isButtonExportPDFDisabled}
            onClick={exportPDF}
          />
        </div>
      </div>
      {state === STATE.IS_TABLE_DATA_LOADING && (
        <div className="h-full flex justify-center items-center">
          <Loader />
        </div>
      )}
      {state === STATE.IS_TABLE_DATA_ERROR && (
        <div className="h-full flex justify-center items-center flex-col gap-3 bg-brand-300/[0.08] rounded-[20px]">
          <div className="flex justify-center items-center flex-col gap-1.5">
            <span className="text-base-1000 text-[22px]/[28px] font-semibold">
              Failed to load data
            </span>
            <span className="text-base-800 text-[16px]/[24px]">
              Error occurred while loading. Try again.
            </span>
          </div>
          <Button
            title="Reload"
            variant="text"
            className="text-destructive-500 !bg-transparent"
            iconStart={<RefreshIcon className="w-5 h-5 text-destructive-500" />}
            onClick={refetchTableData}
          />
        </div>
      )}
      {state === STATE.HAS_TABLE_DATA && renderTable(tableData)}
    </div>
  );
};
